// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * API client for Audit Event endpoints.
 * Provides access to audit event querying, timeline, user activity, and export functionality.
 */

import {
  AuditEvent,
  AuditQueryFilters,
  UserActivityResponse,
  PaginationParams,
  PageResponse,
  ExportFormat
} from '../types/audit';

const API_BASE = '/api/audit';

/**
 * Build query string from filters and pagination params.
 */
function buildQueryString(
  filters?: AuditQueryFilters,
  pagination?: PaginationParams
): string {
  const params = new URLSearchParams();

  if (filters?.brokerageId) {
    params.append('brokerageId', filters.brokerageId);
  }
  if (filters?.category) {
    params.append('category', filters.category);
  }
  if (filters?.action) {
    params.append('action', filters.action);
  }
  if (filters?.severity) {
    params.append('severity', filters.severity);
  }
  if (filters?.userId) {
    params.append('userId', filters.userId);
  }
  if (filters?.resourceType) {
    params.append('resourceType', filters.resourceType);
  }
  if (filters?.resourceId) {
    params.append('resourceId', filters.resourceId);
  }
  if (filters?.startDate) {
    params.append('startDate', filters.startDate);
  }
  if (filters?.endDate) {
    params.append('endDate', filters.endDate);
  }
  if (filters?.searchText) {
    params.append('searchText', filters.searchText);
  }
  if (pagination?.page !== undefined) {
    params.append('page', String(pagination.page));
  }
  if (pagination?.size !== undefined) {
    params.append('size', String(pagination.size));
  }
  if (pagination?.sort) {
    params.append('sort', pagination.sort);
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Audit Events API client.
 */
export const auditApi = {
  /**
   * Query audit events with filtering and pagination.
   */
  async queryEvents(
    filters?: AuditQueryFilters,
    pagination?: PaginationParams
  ): Promise<PageResponse<AuditEvent>> {
    const queryString = buildQueryString(filters, pagination);
    const response = await fetch(`${API_BASE}/events${queryString}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Insufficient permissions to view audit events');
      }
      throw new Error('Failed to fetch audit events');
    }

    return response.json();
  },

  /**
   * Get entity timeline for a specific resource.
   */
  async getEntityTimeline(
    resourceType: string,
    resourceId: string,
    filters?: {
      action?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<AuditEvent[]> {
    const params = new URLSearchParams();
    if (filters?.action) {
      params.append('action', filters.action);
    }
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }

    const queryString = params.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${API_BASE}/timeline/${resourceType}/${resourceId}${suffix}`;

    const response = await fetch(url, {
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Resource not found');
      }
      if (response.status === 403) {
        throw new Error('Insufficient permissions to view timeline');
      }
      throw new Error('Failed to fetch entity timeline');
    }

    return response.json();
  },

  /**
   * Get user activity analysis.
   */
  async getUserActivity(
    userId: string,
    filters?: {
      startDate?: string;
      endDate?: string;
    }
  ): Promise<UserActivityResponse> {
    const params = new URLSearchParams();
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }

    const queryString = params.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${API_BASE}/user/${userId}/activity${suffix}`;

    const response = await fetch(url, {
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Insufficient permissions to view user activity');
      }
      throw new Error('Failed to fetch user activity');
    }

    return response.json();
  },

  /**
   * Export audit events to CSV or JSON.
   */
  async exportEvents(
    format: ExportFormat,
    filters?: Omit<AuditQueryFilters, 'searchText'>
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);

    if (filters?.brokerageId) {
      params.append('brokerageId', filters.brokerageId);
    }
    if (filters?.category) {
      params.append('category', filters.category);
    }
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }

    const queryString = params.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const response = await fetch(`${API_BASE}/export${suffix}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Insufficient permissions to export audit events');
      }
      throw new Error('Failed to export audit events');
    }

    return response.blob();
  }
};
